/**
 * Очікування картки довше за 300 мс (`pendingComponent` маршруту `/r/…`, app/routes/record.ts):
 * скелетон трьох колонок тієї самої форми, що `SkeletonCard`, а назва запису — одразу з рядка
 * списку чи дошки в кеші (baseline А9), коли картку відкрили звідти. Назва — як у вкладки
 * (`recordTitle`): без контактних і `sensitive` полів. Модуль — у першому екрані: лише кеш і метадані.
 * Фази — маршрутного очікування (`RoutePending`): з 3 с від переходу «Довше, ніж зазвичай…», з
 * 10 с — тайм-аут зі «Спробувати ще раз» замість скелетона.
 */
import type { FieldValue } from '@clientline/contracts';
import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useRoutePending } from '../../app/route-pending.ts';
import { RoutePending } from '../../ui/RoutePending.tsx';
import { SkeletonCard } from '../../ui/Skeleton.tsx';
import { platformKeys } from '../api.ts';
import { entityOf, recordTitle, usePlatformMeta } from '../meta.ts';

type Values = Record<string, FieldValue | undefined>;

/**
 * Значення запису з кешу списків і дошок сутності (вони — під `platformKeys.lists`): сторінки
 * `{ pages: [{ items }] }`. Немає — `undefined`.
 */
export function cachedRowValues(
  client: QueryClient,
  entity: string,
  id: string,
): Values | undefined {
  for (const [, data] of client.getQueriesData({ queryKey: platformKeys.lists(entity) })) {
    const pages = (data as { pages?: unknown } | undefined)?.pages;
    if (!Array.isArray(pages)) continue;
    for (const page of pages) {
      const items = (page as { items?: unknown } | undefined)?.items;
      if (!Array.isArray(items)) continue;
      const row = items.find((r) => (r as { id?: unknown } | null)?.id === id) as
        | { values?: Values }
        | undefined;
      if (row?.values) return row.values;
    }
  }
  return undefined;
}

export function CardPending() {
  const { entity: entityKey, recordId } = useParams({ from: '/r/$entity/$recordId' });
  const client = useQueryClient();
  const meta = usePlatformMeta();
  const entity = meta.data ? entityOf(meta.data, entityKey) : undefined;
  const values = entity ? cachedRowValues(client, entityKey, recordId) : undefined;
  const title = entity && values ? recordTitle(entity, values) : undefined;
  const heading = title ? (
    <div className="record__titleblock">
      <h1 className="record__title" title={title} data-testid="card-pending-title">
        {title}
      </h1>
    </div>
  ) : undefined;
  return <RoutePending state={useRoutePending()} skeleton={<SkeletonCard title={heading} />} />;
}
